/**
 * Central date helpers. All formatting and zoning goes through here.
 *
 * Conventions:
 * - Never import `format` from `date-fns` directly — use `fmt`.
 * - Never call `new Date(string)` directly — use `toDate` (or `zoned` for TZ-anchored).
 * - Use `now()` instead of `new Date()` for "current time".
 * - For calendar math (`getMonth`, `startOfWeek`, etc.), pass a `zoned()` value so date-fns
 *   reads the TZ from the TZDate instead of runtime-local.
 */
import { TZDate, tz } from '@date-fns/tz';
import { format, parseISO, setDefaultOptions } from 'date-fns';
import { sv } from 'date-fns/locale';

const TZ = 'Europe/Stockholm';
const ctx = { in: tz(TZ) };

// Pin sv locale + Monday week start for all date-fns calls in the app.
// Per-call options still override these.
setDefaultOptions({ locale: sv, weekStartsOn: 1 });

/**
 * Backend sometimes emits naive `yyyy-MM-dd HH:mm:ss` with no offset.
 * Treat as Stockholm wall-clock until backend is fixed to emit ISO with offset.
 */
const NAIVE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?$/;

/**
 * Parse any date-like input into a `Date`.
 */
const toDate = (date: Date | string | number): Date => {
  if (typeof date === 'string') {
    const m = NAIVE.exec(date);
    if (m) {
      const [, y, mo, d, h, mi, s] = m;
      return new TZDate(+y, +mo - 1, +d, +h, +mi, +s, TZ);
    }
    return parseISO(date);
  }
  return date instanceof Date ? date : new Date(date);
};

/**
 * Format a date in Stockholm + sv locale.
 */
const fmt = (date: Date | string | number, pattern: string): string => format(toDate(date), pattern, ctx);

/** Wrap any date-like value as a Stockholm-anchored `TZDate`. Defaults to "now". */
const zoned = (date: Date | string | number = new Date()): TZDate => new TZDate(toDate(date).getTime(), TZ);

/** Current time as a Stockholm-anchored `TZDate`. Prefer over `new Date()`. */
const now = (): TZDate => zoned(new Date());

/**
 * Build a `TZDate` from calendar components in Stockholm.
 */
const tzd = (year: number, monthIdx = 0, day = 1, hours = 0, minutes = 0, seconds = 0): TZDate =>
  new TZDate(year, monthIdx, day, hours, minutes, seconds, TZ);

export { fmt, now, sv, TZ, toDate, tzd, zoned };
